import React from 'react'
import { useTranslation } from 'react-i18next'
import { LayoutBand, Grid, Cell, H2, Paragraph, Card, Separator, colors, useContainerWidth } from '@fs/zion-ui'
import { ControlSplit, PlaceVillage, SocialFaceHappy, ThingCalendar } from '@fs/zion-icon'
import HelpfulTipsImage from '../assets/HelpfulTips.webp'

interface TipCardProps {
  color: string
  Icon: React.ComponentType<any>
  text: string
}

const TipCard: React.FC<TipCardProps> = ({ color, Icon, text }) => {
  return (
    <Card backgroundColor={colors.gray02} margins="md" edges="none">
      <Grid gutters="sm">
        <Icon color={color} size="md" />
        <Paragraph columns={10} size="sm">
          <strong>{text}</strong>
        </Paragraph>
      </Grid>
    </Card>
  )
}

export default function HelpfulTipsSection(): JSX.Element {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()

  const tips = [
    {
      color: colors.purple50,
      Icon: ThingCalendar,
      textKey: 'tree-overview.family-interview.tips.tip1',
      defaultText: "If they don't remember exact dates, ask for approximate years.",
    },
    {
      color: colors.yellow50,
      Icon: PlaceVillage,
      textKey: 'tree-overview.family-interview.tips.tip2',
      defaultText: "If they don't know the specific town, ask for the province or region.",
    },
    {
      color: colors.blue50,
      Icon: SocialFaceHappy,
      textKey: 'tree-overview.family-interview.tips.tip3',
      defaultText: 'Write down nicknames too - they can help identify people.',
    },
    {
      color: colors.green50,
      Icon: ControlSplit,
      textKey: 'tree-overview.family-interview.tips.tip4',
      defaultText: 'Even partial information is valuable!',
    },
  ]

  return (
    <LayoutBand maxWidth="xxl" marginY="xl" marginX={atWidth({ default: 'sm', xl: 'xxl' })}>
      {/* Main section heading */}
      <H2>{t('tree-overview.family-interview.tips.heading', 'Helpful Tips:')}</H2>

      <Separator size="lg" />

      {/* Two-column responsive grid */}
      <Grid gutters={atWidth({ default: 'md', xl: 'xl' })}>
        {/* Left column: 2x2 grid of tip cards; 1x4 grid at 750px - 1200px */}
        <Cell columns={atWidth({ default: 12, xl: 6 })} order={atWidth({ default: 2, xl: 1 })}>
          <Grid gutters="md">
            {tips.map((tip) => (
              <Cell key={tip.textKey} columns={atWidth({ default: 12, lg: 6, xxl: 3 })}>
                <TipCard color={tip.color} Icon={tip.Icon} text={t(tip.textKey, tip.defaultText)} />
              </Cell>
            ))}
          </Grid>
        </Cell>

        {/* Right column: Image */}
        <Card
          backgroundImage={HelpfulTipsImage}
          columns={atWidth({ default: 12, xl: 6 })}
          minHeight={atWidth({ default: 250, sm: 300, md: 400, lg: 500, xl: 600 })}
          order={atWidth({ default: 1, xl: 2 })}
        >
          {null}
        </Card>
      </Grid>
    </LayoutBand>
  )
}
